import type {
  EstimateRequest,
  Money,
  RentVsBuyAssumptions,
  RentVsBuyComparison,
} from "./agent-types";
import { ApiError, apiRequest } from "./api";
import { isAbortError } from "./estimates";

export type RentVsBuyPreviewRequest = {
  estimate: EstimateRequest;
  monthly_rent: Money;
  years_in_home?: number;
  assumptions?: RentVsBuyAssumptions;
};

export type RentVsBuyPreviewResponse = {
  comparison: RentVsBuyComparison;
};

export class RentVsBuyPreviewError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RentVsBuyPreviewError";
  }
}

export class RentVsBuyUnauthorizedError extends RentVsBuyPreviewError {
  constructor(message = "Please sign in again to refresh rent-vs-buy.") {
    super(message);
    this.name = "RentVsBuyUnauthorizedError";
  }
}

export class RentVsBuyValidationError extends RentVsBuyPreviewError {
  constructor(public details: string) {
    super(details || "validation failed");
    this.name = "RentVsBuyValidationError";
  }
}

export class RentVsBuyServiceUnavailableError extends RentVsBuyPreviewError {
  constructor() {
    super("rent-vs-buy service unavailable");
    this.name = "RentVsBuyServiceUnavailableError";
  }
}

export class RentVsBuyNetworkError extends RentVsBuyPreviewError {
  constructor(public cause: unknown) {
    super("network error");
    this.name = "RentVsBuyNetworkError";
  }
}

export type FetchRentVsBuyOptions = {
  token: string;
  signal?: AbortSignal;
};

export async function fetchRentVsBuyPreview(
  req: RentVsBuyPreviewRequest,
  opts: FetchRentVsBuyOptions,
): Promise<RentVsBuyPreviewResponse> {
  try {
    return await apiRequest<RentVsBuyPreviewResponse>(
      "/api/rent-vs-buy/preview",
      {
        method: "POST",
        body: req,
        token: opts.token,
        signal: opts.signal,
      },
    );
  } catch (err) {
    if (isAbortError(err)) throw err;
    if (err instanceof ApiError) {
      if (err.status === 401) {
        throw new RentVsBuyUnauthorizedError(err.message);
      }
      if (err.status === 422 || err.status === 400) {
        throw new RentVsBuyValidationError(err.message);
      }
      if (err.status === 503) {
        throw new RentVsBuyServiceUnavailableError();
      }
      throw new RentVsBuyPreviewError(err.message);
    }
    throw new RentVsBuyNetworkError(err);
  }
}
